import React from 'react'

import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { InputFooter } from '@amzn/stencil-react-components/form'
import { IconPlus } from '@amzn/stencil-react-components/icons'
import { Col, Row, Spacer, View } from '@amzn/stencil-react-components/layout'
import { useBreakpoints } from '@amzn/stencil-react-components/responsive'
import { H5 } from '@amzn/stencil-react-components/text'

import { IconWithTooltip } from 'src/components/IconWithTooltip'
import { ItemEditorBasicInfo } from 'src/components/ItemEditorBasicInfo'
import { MediaManager } from 'src/components/MediaContentsEditor/MediaManager'
import { useItemEntity, useValidationErrorEntity } from 'src/hooks/useEntity'
import { useSetLocaleWiseMediaOpt } from 'src/hooks/useSetLocaleWiseMediaOpt'
import { AvatarDTO, VirtualMeetingDTO } from 'src/models/dto/items/VirtualMeetingDTO'
import { getListOfFileTypes, MediaFileGroups } from 'src/models/media/MediaTemplateDTO'
import { ItemEditorProps } from 'src/pages/module-builder/item-editors/ItemEditorContainer'
import {
    ItemEditorNumberInput,
    ItemEditorTextInput,
} from 'src/pages/module-builder/item-editors/ItemEditorInputs'
import { ItemEditorValidationErrors } from 'src/pages/module-builder/item-editors/ItemEditorValidationErrors'
import {
    MAX_NUMBER_OF_ATTENDEES,
    VirtualMeetingAttendeeItem,
} from 'src/pages/module-builder/item-editors/VirtualMeetingAttendeeItem'
import { VirtualMeetingHandler } from 'src/services/EntityServices/ItemUpdateHandlers/VirtualMeetingHandler'

export const VirtualMeetingItemEditor = ({
    itemId,
    locale,
    workflowEntityId,
    showValidationError,
    editDisabled,
}: ItemEditorProps) => {
    const { entity: itemDTO } = useItemEntity<VirtualMeetingDTO>({
        entityId: itemId,
        workflowEntityId,
    })

    const { entity: validationErrorEntity } = useValidationErrorEntity(itemId)

    const { matches } = useBreakpoints()

    const flexItemWidth = matches.s ? '100%' : 'default'

    const setLocaleWiseMediaOpt = useSetLocaleWiseMediaOpt(itemDTO)

    const attendeesHeaderId = `${itemId}-attendees-header`

    return (
        <>
            <ItemEditorValidationErrors {...{ itemId, showValidationError }} />
            <H5 color='neutral100' fontWeight='bold'>
                Meeting Details
            </H5>
            <ItemEditorBasicInfo
                handler={VirtualMeetingHandler}
                itemId={itemId}
                workflowEntityId={workflowEntityId}
                locale={locale}
                editDisabled={editDisabled}
                showValidationError={showValidationError}
                disableOptional={true}
            />
            <Spacer height={'S200'} />
            <Row alignItems={'flex-start'}>
                <Col flexGrow={1} width={flexItemWidth}>
                    <ItemEditorTextInput
                        dataTestId={'title'}
                        inputId={'title'}
                        disabled={editDisabled}
                        value={itemDTO.titleI18N[locale] ?? ''}
                        placeholder={'Enter a meeting title'}
                        itemId={itemDTO.id}
                        setValue={(nextValue: string) => {
                            VirtualMeetingHandler.updateTitle(itemDTO.id, locale, nextValue)
                        }}
                        validationErrorMessage={(
                            validationErrorEntity.validationErrors.titleI18N ?? []
                        ).join(', ')}
                        showError={showValidationError}
                        labelText={'Meeting title'}
                        maxCharacterLimit={70}
                    />
                </Col>
            </Row>
            <Spacer height='S400' />
            <Row alignItems={'flex-start'}>
                <Col alignItems={'center'} height={'100%'}>
                    <Spacer height='S100' />
                    <H5 id={attendeesHeaderId}>Attendees</H5>
                </Col>
                <IconWithTooltip tooltipText={'Omit the job candidate'} />
            </Row>
            <View
                border={matches.s ? '0px' : '1px solid #B9C0C8'}
                role='list'
                aria-labelledby={attendeesHeaderId}
            >
                {/*For each avatar, display title banner, 4 text prompts, and add profile image feature*/}
                {itemDTO.avatars.map((avatar: AvatarDTO, avatarIndex: number, { length }) => (
                    <VirtualMeetingAttendeeItem
                        key={avatarIndex}
                        avatar={avatar}
                        avatarIndex={avatarIndex}
                        itemId={itemDTO.id}
                        locale={locale}
                        totalAttendees={length}
                        editDisabled={editDisabled}
                        showValidationError={showValidationError}
                        validationErrors={validationErrorEntity.validationErrors}
                    />
                ))}
                <Row alignItems='flex-end' width='100%' padding={matches.s ? undefined : 'S200'}>
                    {!matches.s && <Spacer flex='1 1 0' />}
                    <View flex={matches.s ? '1 1 0' : undefined}>
                        <Button
                            style={{ width: '100%' }}
                            dataTestId={'add-avatars'}
                            icon={!matches.s ? <IconPlus aria-hidden /> : <></>}
                            variant={!matches.s ? ButtonVariant.Tertiary : ButtonVariant.Secondary}
                            onClick={() => {
                                VirtualMeetingHandler.addAvatar(itemDTO.id)
                            }}
                            aria-disabled={
                                itemDTO.avatars.length === MAX_NUMBER_OF_ATTENDEES || editDisabled
                            }
                        >
                            Add attendee
                        </Button>
                    </View>
                </Row>
            </View>
            <Spacer height='S400' />
            <H5 color='neutral100' fontWeight='bold'>
                Meeting audio recording
            </H5>
            <MediaManager
                itemId={itemDTO.id}
                locale={locale}
                localeWiseMediaOpt={itemDTO.localeWiseMedia}
                setLocaleWiseMediaOpt={setLocaleWiseMediaOpt}
                disableMediaExpander={true}
                maxNumberOfMedia={1}
                buttonText={'Add audio clip'}
                buttonVariant={ButtonVariant.Secondary}
                lockedFileTypes={getListOfFileTypes(MediaFileGroups.AUDIO)}
                disabled={editDisabled}
            />
            {validationErrorEntity.validationErrors.localeWiseMedia && (
                <div>
                    <InputFooter style={{ visibility: 'visible' }} error id={'audio-clip-footer'}>
                        {validationErrorEntity.validationErrors.localeWiseMedia.join(', ')}
                    </InputFooter>
                    <Spacer height='S400' />
                </div>
            )}
            <Col flexGrow={1} width={matches.s ? '100%' : '30%'}>
                <ItemEditorNumberInput
                    dataTestId={'playback-limit'}
                    inputId={'playback-limit'}
                    value={itemDTO.playbackLimit ?? null}
                    disabled={editDisabled}
                    placeholder={'E.g. 3'}
                    itemId={itemDTO.id}
                    setValue={(nextValue) => {
                        VirtualMeetingHandler.updatePlaybackLimit(itemDTO.id, nextValue)
                    }}
                    validationErrorMessage={(
                        validationErrorEntity.validationErrors.playbackLimit ?? []
                    ).join(', ')}
                    showError={showValidationError}
                    labelText={'Max play count (optional)'}
                />
            </Col>
        </>
    )
}
